import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { OfferCard } from "@/components/offers/OfferCard";
import { OFFERS } from "@/components/offers/offer-data";
import { openBookingWidget } from "@/components/BookingButton";
import { pressLogos } from "@/data/social-proof";
import wordmark from "@/assets/backland-wordmark.svg";
import heroImg from "@/assets/path-hero.jpg.asset.json";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const URL = "https://www.travelbackland.com/offers";
const TITLE = "Fall in Love & Family Adventure Offers | Backland Arizona";
const DESC =
  "Book 2+ nights at Backland and get the Romance Package free + $100 off the Stay & Dine rate, or $200 off the Family Stay & Dine rate. Use codes FALLINLOVE or ADVENTUREFAM.";

export const Route = createFileRoute("/offers")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:image", content: heroImg.url },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: OffersLanding,
});

const VALUE_STRIP = [
  "Only 10 suites",
  "160 private acres",
  "Dark skies",
  "On-site dining",
];

const FAQ: { question: string; answer: string }[] = [
  {
    question: "How do I redeem these offers?",
    answer:
      "Tap Book Now on the offer you want, paste the code (FALLINLOVE or ADVENTUREFAM) on the first booking screen, and choose two or more nights.",
  },
  {
    question: "Can I combine the two offers?",
    answer:
      "No. Each offer stands on its own and cannot be combined with other promotions or discounts.",
  },
  {
    question: "Is there a minimum stay?",
    answer:
      "Yes — both offers require a two-night minimum stay and are subject to availability.",
  },
  {
    question: "Do these offers apply to any room rate?",
    answer:
      "No — these offers apply only to the Stay & Dine rate. Select the Stay & Dine option on the booking screen, then paste the promo code before choosing your dates.",
  },
  {
    question: "Where is Backland located?",
    answer:
      "Backland is a luxury tented resort in Northern Arizona, in the cool ponderosa pine high country between Williams and Flagstaff — about an hour from the Grand Canyon South Rim. We are 8 miles into the Kaibab National forest surrounded by 300,000 acres of wilderness. No highway noise, no city lights, just vast dark skies and the quiet stillness of nature.\u00a0",
  },
  {
    question: "What if I need to change my dates?",
    answer:
      "Reach out to our team as soon as you know. We'll do our best to move your reservation, subject to availability of the offer and your suite type.",
  },
];

function OffersLanding() {
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "lp_view",
      landing_page: "/offers",
      offer_code: "FALL_FAMILY",
    });
  }, []);

  const onHeroBook = () => {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "lp_book_click",
        landing_page: "/offers",
        offer_code: "FALL_FAMILY",
      });
    }
    openBookingWidget().catch(() => {});
  };

  return (
    <div className="fixed inset-0 overflow-y-auto overscroll-contain no-scrollbar bg-background text-foreground pb-28 md:pb-8">
      {/* HERO */}
      <section className="relative w-full min-h-[80vh] sm:min-h-[70vh] flex items-end">
        <img
          src={heroImg.url}
          alt="Backland at dusk in the ponderosa pines"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        <div className="relative z-10 w-full max-w-3xl mx-auto px-6 pb-12 md:pb-20 pt-24 text-center">
          <img
            src={wordmark}
            alt="Backland"
            className="mx-auto mb-6 w-32 md:w-40"
            draggable={false}
          />
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-4">
            LIMITED-TIME OFFERS
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-foreground">
            <em>Fall</em> in Love
            <br />&amp;
            <br />
            Family Adventure
          </h1>
          <p className="mt-4 text-base md:text-lg text-foreground/80 font-light max-w-xl mx-auto">
            Escape to cool ponderosa pines, dark skies, and 160 private acres
            near the Grand Canyon. Two offers — pick the one that fits your trip.
          </p>
          <div className="mt-7 flex justify-center">
            <button
              type="button"
              onClick={onHeroBook}
              className="font-serif italic text-base md:text-lg text-background bg-gold/95 hover:bg-gold px-8 py-4 rounded-full tracking-wide transition min-h-[56px]"
            >
              Book Now
            </button>
          </div>
          <p className="mt-3 text-xs text-foreground/60 font-light">
            See both offers below.
          </p>
        </div>
      </section>

      {/* OFFER CARDS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 md:pt-16 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {OFFERS.map((offer) => (
            <OfferCard key={offer.id} offer={offer} />
          ))}
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <ul className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-sm md:text-base text-foreground/80 font-light">
          {VALUE_STRIP.map((v, i) => (
            <li key={v} className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-gold/80" />
              <span>{v}</span>
              {i < VALUE_STRIP.length - 1 && (
                <span className="hidden md:inline text-foreground/25 ml-5">
                  ·
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* SOCIAL PROOF */}
      <section className="max-w-4xl mx-auto px-6 pb-16 md:pb-20 text-center">
        <p className="font-serif italic text-xl md:text-2xl text-foreground/90 leading-snug">
          "A rare kind of quiet luxury — the dark sky here doesn't just
          sparkle, it overwhelms."
        </p>
        <p className="mt-3 text-[11px] uppercase tracking-[0.3em] text-foreground/55">
          4.9 ★ VERIFIED GUEST REVIEWS
        </p>
        <div className="mt-8 flex flex-wrap justify-center items-center gap-x-6 gap-y-3 opacity-65">
          {pressLogos.slice(0, 6).map((p) =>
            p.logo ? (
              <img
                key={p.name}
                src={p.logo}
                alt={p.name}
                className="h-5 md:h-6 w-auto object-contain"
                loading="lazy"
              />
            ) : null,
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-2xl mx-auto px-6 pb-20">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-4 text-center">
          Offer FAQ
        </div>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="border-b border-foreground/10"
            >
              <AccordionTrigger className="text-sm md:text-base font-medium text-foreground py-4 hover:no-underline hover:text-gold/90 transition text-left">
                <span className="flex items-center gap-2 flex-1">
                  {item.question}
                </span>
                
              </AccordionTrigger>
              <AccordionContent className="text-sm text-foreground/80 font-light leading-relaxed pb-4">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-foreground/10 py-10 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-base md:text-lg uppercase tracking-[0.25em] text-foreground border border-foreground/30 hover:border-gold/70 rounded-full px-8 py-3.5 transition"
        >
          BACKLAND HOME →
        </Link>
      </footer>

      {/* STICKY MOBILE CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-3 bg-gradient-to-t from-background via-background/95 to-background/0">
        <button
          type="button"
          onClick={onHeroBook}
          className="w-full font-serif italic text-base text-background bg-gold/95 hover:bg-gold px-6 py-3.5 rounded-full tracking-wide transition min-h-[52px] shadow-lg"
        >
          Book Now
        </button>
      </div>
    </div>
  );
}
